import { useCallback, useEffect, useState } from '@wordpress/element';
import { SelectControl, PanelBody } from '@wordpress/components';
import moment from 'moment';
import {
  generateVimeoIframeNonAttributes,
  parseHubSpotShortcode,
} from '../utils/methods';
import { initCopy } from '../../../resources/scripts/module/utils';
import $ from 'jquery';

const { useSelect } = wp.data;
const { registerBlockType } = wp.blocks;
const { InspectorControls, InnerBlocks, RichText, URLInputButton } =
  wp.blockEditor;
const { Button } = wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

registerBlockType('pg/article-content-post', {
  title: 'PG Article Content Block',
  icon: 'calendar',
  category: 'layout',
  attributes: {
    title: { type: 'string' },
    excerpt: { type: 'string' },
    tags: { type: 'string', default: '' },
    copyHeader: { type: 'string', default: 'Share post' },
    formHeader: {
      type: 'string',
      default: 'Stay up to date with the latest ESG-trends.',
    },
    articleFormCode: { type: 'string', default: '' },
    content: {
      type: 'string',
      default: '',
    },
    people: {
      type: 'array',
      default: [],
    },
    peopleJson: { type: 'string', default: '[]' },
    aboutHeader: { type: 'string', default: '' },
    aboutContent: {
      type: 'string',
      default: '',
    },
    aboutUrlLabel: { type: 'string', default: 'More' },
    aboutUrl: { type: 'string', default: '' },
  },

  edit: ({ attributes, setAttributes }) => {
    const { people } = attributes;

    const currentPost = useSelect(
      (select) => select('core/editor').getCurrentPost(),
      null
    );
    const postLink = currentPost && currentPost.link;
    const postCreationDate = currentPost && currentPost.date;

    const acfFields = acf.getFields();

    const categories = useSelect(
      (select) => select('core/editor').getEditedPostAttribute('categories'),
      []
    );

    const tags = useSelect((select) => {
      const { getEditedPostAttribute } = select('core/editor');
      const tagIds = getEditedPostAttribute('tags');

      if (!tagIds || tagIds.length === 0)
        return 'Add some tags if you need it...';

      return (
        select('core')
          .getEntityRecords('taxonomy', 'post_tag', {
            include: tagIds,
            per_page: -1,
          })
          ?.map((tag) => tag.name)
          .join(', ') || 'Loading tags...'
      );
    }, []);

    const metaData = useSelect(
      (select) => select('core/editor').getEditedPostAttribute('meta'),
      []
    );
    const [loaded, setLoaded] = useState(false);
    const [changed, setChanged] = useState(false);

    useEffect(() => {
      if (loaded && attributes.articleFormCode && window.hbspt) {
        const { portalId, formId } = parseHubSpotShortcode(
          attributes.articleFormCode
        );

        if (portalId && formId) {
          const formClassTarget = `.my-hubspot-form-${formId}`;
          window.hbspt.forms.create({
            portalId: portalId,
            formId: formId,
            target: formClassTarget,
          });
        }
      }
    }, [changed, loaded, attributes.articleFormCode, window.hbspt]);

    const loadHubSpotScript = () => {
      if (window.hbspt) {
        setLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = '//js.hsforms.net/forms/v2.js';
      script.onload = () => setLoaded(true);
      document.body.appendChild(script);
    };

    useEffect(() => {
      loadHubSpotScript();
    }, []);

    const setAttributesData = () => {
      setAttributes({
        articleFormCode:
          metaData.article_form_shortcode ||
          metaData.form_shortcode_of_webinar ||
          null,
      });
      setChanged(!changed);
    };

    const refreshMeta = useCallback(
      (event, field) => {
        event.preventDefault();
        event.stopPropagation();

        if (field.data.type === 'date_picker') {
          metaData[field.data.name] = moment(
            event.target.value,
            'DD/MM/YYYY'
          ).format('YYYYMMDD');
        } else {
          metaData[field.data.name] = event.target.value;
        }

        setAttributesData();
      },
      [metaData]
    );

    useEffect(() => {
      setAttributesData();
      acfFields.forEach((field) =>
        $(`[name=acf\\[${field.data.key}\\]]`).trigger('change')
      );
      acfFields.forEach((field) =>
        $(`[name=acf\\[${field.data.key}\\]]`).on('change', (event) =>
          refreshMeta(event, field)
        )
      );
    }, [categories, metaData]);

    useEffect(() => {
      // Function to refresh all ACF fields
      const refreshAllACFFields = () => {
        // Check if metaData is available and valid
        if (!metaData) {
          return;
        }

        acfFields.forEach((field) =>
          $(`[name=acf\\[${field.data.key}\\]]`).trigger('change')
        );
      };

      if (!loaded) {
        refreshAllACFFields();
        setLoaded(true);
      }
    }, [categories, attributes, metaData, loaded]);

    useEffect(() => {
      if (loaded && attributes.articleFormCode && window.hbspt) {
        const { portalId, formId } = parseHubSpotShortcode(
          attributes.articleFormCode
        );

        if (portalId && formId) {
          const formClassTarget = `.my-hubspot-form-${formId}`;
          window.hbspt.forms.create({
            portalId: portalId,
            formId: formId,
            target: formClassTarget,
          });
        }
      }
    }, [changed, loaded, attributes.articleFormCode]);

    useEffect(() => {
      initCopy();
    }, [loaded]);

    const allPeople = useSelect((select) => {
      return select('core').getEntityRecords('postType', 'people', {
        per_page: -1,
      });
    }, []);

    const peopleOptions = allPeople
      ? allPeople.map((person) => ({
          label: person.acf?.person_name || person.title.rendered,
          value: person.id,
        }))
      : [];

    const updatePeople = (selectedIds) => {
      setAttributes({
        people: selectedIds.map(Number),
        peopleJson: JSON.stringify(selectedIds.map(Number)),
      });
    };

    const removePerson = (personId) => {
      const updatedPeople = people.filter((id) => id !== personId);
      setAttributes({ people: updatedPeople });
      setAttributes({ peopleJson: JSON.stringify(updatedPeople) });
    };

    return (
      <Fragment>
        <InspectorControls>
          <PanelBody title={__('Select People')}>
            <SelectControl
              multiple
              label={__('Select People')}
              value={people}
              options={peopleOptions}
              onChange={updatePeople}
            />
          </PanelBody>
        </InspectorControls>

        <div className="editor-component">
          <section className="section-light-primary">
            <article className={`post post-webinar`}>
              <div className={'left'}>
                <header>
                  <div className="meta">
                    <span className="article-tag">
                      {tags ?? 'Add some tags if you need it...'}
                    </span>
                    {loaded && (
                      <time
                        className="dt-published"
                        dateTime={moment(postCreationDate ?? null).format()}
                      >
                        {moment(postCreationDate ?? null).format(
                          'DD MMMM YYYY'
                        )}
                      </time>
                    )}
                  </div>

                  <RichText
                    tagName="h1"
                    value={attributes.title}
                    className={'title'}
                    placeholder={__('Title...')}
                    onChange={(value) => setAttributes({ title: value })}
                  />
                  <RichText
                    tagName="div"
                    className={'excerpt'}
                    placeholder={__('Post Excerpt..')}
                    value={attributes.excerpt}
                    onChange={(value) => setAttributes({ excerpt: value })}
                  />
                </header>

                <div className={'content'}>
                  <InnerBlocks
                    allowedBlocks={[
                      'core/paragraph',
                      'core/image',
                      'core/heading',
                      'core/list',
                      'core/embed',
                      'core/table',
                      'core/freeform',
                      'core/quote',
                      'pg/quote',
                      'pg/button',
                      'pg/banner',
                      'pg/list',
                    ]}
                    template={[
                      [
                        'core/paragraph',
                        {
                          placeholder:
                            'Start writing or type / to choose a block',
                        },
                      ],
                      ['core/image'],
                    ]}
                  />
                </div>
              </div>
              <div className="right">
                <div
                  className="people-list-block"
                  data-people={attributes.peopleJson}
                >
                  {loaded && allPeople && (
                    <div className="cards">
                      {people.map((personId) => {
                        const personDetails = allPeople.find(
                          (p) => p.id === personId
                        );
                        return (
                          <div className="person-card" key={personId}>
                            <div className="image">
                              <img
                                src={personDetails?.featured_image_url}
                                alt={personDetails?.acf.person_name}
                              />
                            </div>
                            <div className="data relative">
                              <p className="name">
                                {personDetails.acf.person_name}
                              </p>
                              <p className="title">
                                {personDetails.acf.person_title}
                              </p>
                              <p className="company">
                                {personDetails.acf.person_company}
                              </p>
                              <Button
                                className={'personRemoveButton'}
                                isDestructive
                                onClick={() => removePerson(personId)}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="share">
                  <RichText
                    tagName="h6"
                    value={attributes.copyHeader}
                    placeholder={__('Share post header....')}
                    onChange={(value) => setAttributes({ copyHeader: value })}
                  />
                  {loaded && (
                    <p>
                      <a
                        href={postLink ?? '#'}
                        className="share-copy copy-link"
                      >
                        Copy link
                      </a>
                      <a
                        href={
                          postLink
                            ? `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(postLink)}`
                            : '#'
                        }
                        className="share-linkedin"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Linkedin
                      </a>
                      <a
                        href={
                          postLink
                            ? `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postLink)}`
                            : '#'
                        }
                        className="share-facebook"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Facebook
                      </a>
                    </p>
                  )}
                </div>

                <div className="green-box">
                  <RichText
                    tagName="h4"
                    value={attributes.formHeader}
                    placeholder={__('Form header....')}
                    onChange={(value) => setAttributes({ formHeader: value })}
                  />
                  <div
                    className={`my-hubspot-form-${parseHubSpotShortcode(attributes.articleFormCode).formId || ''}`}
                  ></div>
                </div>

                <div className="about">
                  <RichText
                    tagName="h6"
                    value={attributes.aboutHeader}
                    placeholder={__('About header....')}
                    onChange={(value) => setAttributes({ aboutHeader: value })}
                  />
                  <RichText
                    tagName="div"
                    className={'about--info'}
                    placeholder={__('About content..')}
                    value={attributes.aboutContent}
                    onChange={(value) => setAttributes({ aboutContent: value })}
                  />
                  <a href="#" className={`btn btn-dark btn-text`}>
                    <RichText
                      tagName="span"
                      value={attributes.aboutUrlLabel}
                      onChange={(newLabel) =>
                        setAttributes({ aboutUrlLabel: newLabel })
                      }
                      placeholder={__('Button Label', 'pg')}
                    />
                    <URLInputButton
                      url={attributes.aboutUrl}
                      onChange={(newLink) =>
                        setAttributes({ aboutUrl: newLink })
                      }
                    />
                  </a>
                </div>
              </div>
            </article>
          </section>
        </div>
      </Fragment>
    );
  },

  save: ({ attributes }) => {
    const {
      title,
      excerpt,
      tags,
      copyHeader,
      formHeader,
      peopleJson,
      articleFormCode,
      aboutHeader,
      aboutContent,
      aboutUrlLabel,
      aboutUrl,
    } = attributes;

    return (
      <div className="editor-component">
        <section className="section-light-primary">
          <article className="post post-webinar">
            <div className="left">
              <header>
                <div className="meta">
                  <span className="article-tag">{tags}</span>
                </div>
                <h1 className="title">{title}</h1>
                <div className="excerpt">{excerpt}</div>
              </header>
              <div className="content">
                <InnerBlocks.Content />
              </div>
            </div>
            <div className="right">
              <div className="people-list-block" data-people={peopleJson}></div>
              <div className="share">
                <h6>{copyHeader}</h6>
              </div>
              <div className="green-box">
                <h4>{formHeader}</h4>
                <div
                  className={`my-hubspot-form-${parseHubSpotShortcode(articleFormCode).formId || ''}`}
                ></div>
              </div>
              <div className="about">
                <h6>{aboutHeader}</h6>
                <div
                  className={'about--info'}
                  dangerouslySetInnerHTML={{
                    __html: aboutContent,
                  }}
                ></div>
                <a href={aboutUrl} className={`btn btn-dark btn-text`}>
                  {aboutUrlLabel}
                </a>
              </div>
            </div>
          </article>
        </section>
      </div>
    );
  },
});
